import { FormConfig } from '@ibe/components';
import { ApiTravelerType } from '@ibe/api';
import Keys from '@/Translations/generated/en/traveller-builder.json.keys';
import { TFunction } from 'i18next';
import dayjs from 'dayjs';
import { ADULT_START_AGE, CHILD_MAX_AGE } from '@/Config/travellerFormConfig';

const randomNumber = (): number => Math.floor(Math.random() * 5);

export const getTravelerMockDataAgent = (): Record<string, unknown> => {
  return {
    salutation: 'MR',
    firstname: ['Firstname1', 'Firstname2', 'Firstname3', 'Firstname4', 'Firstname5'][
      randomNumber()
    ],
    lastname: ['Lastname1', 'Lastname2', 'Lastname3', 'Lastname4', 'Lastname5'][randomNumber()],
    birthDate: ['1960-06-26', '1970-07-27', '1980-08-28', '1990-09-29', '2000-10-30'][
      randomNumber()
    ],
    phone: ['93874054', '89605456', '23409857', '689098345', '0749385765'][randomNumber()]
  };
};
const travellerFormConfigAgent = (
  isFirstTraveler: boolean,
  travelerType: ApiTravelerType,
  t: TFunction,
  initialValues?: { [key: string]: string },
  isMobileView?: boolean
): FormConfig => {
  const childrenYear = dayjs()
    .subtract(parseInt(initialValues?.age || '0'), 'years')
    .format('YYYY');
  let formConfig: FormConfig = {
    rows: [
      {
        key: 'row1',
        elements: [
          {
            tag: 'select',
            key: 'salutation',
            placeholder: t(Keys.salutation),
            externalDataOptions: 'salutations',
            rowGrid: {
              md: 6
            },
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                }
              ]
            }
          },
          {
            tag: 'input',
            key: 'firstname',
            placeholder: t(Keys.firstname),
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                },
                {
                  type: 'onlyNumbersNotAllowed',
                  customMessage: t(Keys.onlyNumbersNotAllowed)
                },
                {
                  type: 'onlySpacesNotAllowed',
                  customMessage: t(Keys.onlySpacesNotAllowed)
                },
                {
                  type: 'customRegExValidation',
                  customMessage: t(Keys.enterValidName),
                  parameter:
                    /^(?!.*[!@#$%^&*()_+=\[\]{};:"\\|,.<>?/])(?:[A-Za-zÀ-ÖØ-öø-ÿ'-]+|\b\d+\b)(?:\s(?:[A-Za-zÀ-ÖØ-öø-ÿ'-]+|\b\d+\b))*$/
                }
              ]
            },
            rowGrid: {
              md: 6
            }
          },
          {
            tag: 'input',
            key: 'lastname',
            placeholder: t(Keys.lastname),
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                },
                {
                  type: 'onlyNumbersNotAllowed',
                  customMessage: t(Keys.onlyNumbersNotAllowed)
                },
                {
                  type: 'onlySpacesNotAllowed',
                  customMessage: t(Keys.onlySpacesNotAllowed)
                },
                {
                  type: 'customRegExValidation',
                  customMessage: t(Keys.enterValidName),
                  parameter:
                    /^(?!.*[!@#$%^&*()_+=\[\]{};:"\\|,.<>?/])(?:[A-Za-zÀ-ÖØ-öø-ÿ'-]+|\b\d+\b)(?:\s(?:[A-Za-zÀ-ÖØ-öø-ÿ'-]+|\b\d+\b))*$/
                }
              ]
            },
            rowGrid: {
              md: 6
            }
          },
          {
            tag: 'datePicker',
            key: 'birthDate',
            placeholder: t(Keys.dateOfBirth),
            withKeyboardSupport: true,
            showInModal: isMobileView,
            modalLabel: t(Keys.modalLabel),
            rowGrid: {
              md: 6
            },
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                },
                ...(travelerType === ApiTravelerType.ADULT
                  ? [
                      {
                        type: 'isOlderThanAgeOnDateString',
                        customMessage: t(Keys.errorInvalidAdult)
                      }
                    ]
                  : travelerType === ApiTravelerType.CHILD
                    ? [
                        {
                          type: 'isYoungerThanAgeOnDateString',
                          customMessage: t(Keys.errorInvalidChild)
                        }
                      ]
                    : travelerType === ApiTravelerType.INFANT
                      ? [
                          {
                            type: 'isInfantOnDateString',
                            customMessage: t(Keys.errorInvalidInfant)
                          }
                        ]
                      : [])
              ]
            }
          }
        ]
      }
    ]
  };
  const phoneElement = {
    tag: 'input',
    key: 'phone',
    placeholder: t(Keys.phone),
    validations: {
      schema: 'string',
      types: isFirstTraveler
        ? [
            {
              type: 'required',
              customMessage: t(Keys.inputRequired)
            },
            {
              type: 'customRegExValidation',
              customMessage: t(Keys.enterValidPhoneNumber),
              parameter: /^\+?[0-9]{6,15}$/
            }
          ]
        : []
    },
    rowGrid: {
      md: 6
    }
  } as FormConfig['rows'][0]['elements'][0];

  const emailElement = {
    tag: 'input',
    key: 'email',
    placeholder: t(Keys.email),
    validations: {
      schema: 'string',
      types: [
        {
          type: 'email',
          customMessage: t(Keys.errorEmail)
        }
      ]
    },
    rowGrid: {
      md: 6
    }
  } as FormConfig['rows'][0]['elements'][0];

  if (travelerType === ApiTravelerType.ADULT && isFirstTraveler) {
    return {
      ...formConfig,
      rows: formConfig.rows.map(row =>
        row.key === 'row1'
          ? {
              ...row,
              elements: [...row.elements, phoneElement, emailElement]
            }
          : row
      )
    };
  }

  return formConfig;
};

export default travellerFormConfigAgent;
